import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { updateAttractionImage } from '~/services/AttractionsService';

import type { Attraction } from '~/types/attractionTypes';

import { attractionQueryKeys } from './attractionQueryKeys';

/**
 * Sets the hero image for an attraction.
 * Uses optimistic update so the image appears instantly, then
 * marks detail stale and invalidates list queries for eventual consistency.
 */
export function useUpdateAttractionImage() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      attractionId,
      imageId,
      imageUrl,
    }: {
      attractionId: string;
      imageId: string;
      imageUrl: string;
    }) => {
      return await updateAttractionImage(attractionId, imageId, imageUrl);
    },
    onMutate: async ({ attractionId, imageUrl }) => {
      // Cancel in-flight queries so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: attractionQueryKeys.detail(attractionId) });

      // Snapshot current cache for rollback
      const previousAttraction = queryClient.getQueryData<Attraction>(attractionQueryKeys.detail(attractionId));

      // Optimistic update: set imageUrl immediately
      if (previousAttraction) {
        queryClient.setQueryData<Attraction>(attractionQueryKeys.detail(attractionId), {
          ...previousAttraction,
          imageUrl,
        });
      }

      return { previousAttraction };
    },
    onSuccess: (_data, { attractionId }) => {
      // Mark detail as stale without immediate refetch to avoid CDN cache
      // serving the pre-update image and reverting the optimistic update.
      queryClient.invalidateQueries({
        queryKey: attractionQueryKeys.detail(attractionId),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
      enqueueSnackbar('Hero image updated successfully', { variant: 'success' });
    },
    onError: (error: Error, { attractionId }, context) => {
      // Rollback optimistic update on failure
      if (context?.previousAttraction) {
        queryClient.setQueryData(attractionQueryKeys.detail(attractionId), context.previousAttraction);
      }
      enqueueSnackbar(`Error updating image: ${error.message || 'Unknown error'}`, { variant: 'error' });
    },
  });
}
